import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Trash2, Download, Mail } from "lucide-react";

const DataDeletionPolicyPage = () => (
  <section className="bg-cream-100 py-16">
    <div className="max-w-3xl mx-auto px-5 lg:px-10">
      <Link to="/" className="text-navy-700/70 hover:text-coral-300 inline-flex items-center gap-1 text-sm mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to home
      </Link>
      <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">Legal</p>
      <h1 className="font-serif text-navy-700 text-4xl md:text-5xl mt-2">
        Data Deletion <span className="italic">Policy</span>
      </h1>
      <p className="text-sm text-navy-700/60 mt-2">
        Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
      </p>

      <div className="max-w-none mt-10 space-y-8 text-navy-700/85 leading-relaxed">
        <p className="text-lg">
          This policy explains how we retain, delete, and let you delete your data on galeriecop. It supports your
          right-to-erasure under GDPR / UK-GDPR.
        </p>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">1. Delete your account yourself</h2>
          <p>Signed-in hosts can self-delete anytime. It&apos;s a one-click flow:</p>
          <ol className="list-decimal pl-6 mt-2 space-y-1.5">
            <li>Go to your <Link to="/dashboard" className="text-coral-400 hover:text-coral-500">dashboard</Link>.</li>
            <li>Scroll to <strong>Privacy &amp; your data</strong>.</li>
            <li>Click <strong>Delete my account</strong> and type &quot;DELETE&quot; to confirm.</li>
          </ol>
          <p className="mt-3">
            Everything associated with your account is removed immediately from our live systems — profile,
            events, uploaded photos/videos, guestbook messages, RSVPs, payment history, referral records, tokens.
          </p>
          <p className="mt-3">
            <Link
              to="/dashboard"
              data-testid="deletion-goto-dashboard"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm"
            >
              <Trash2 className="w-4 h-4" /> Go to my dashboard
            </Link>
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">2. Delete by email</h2>
          <p>
            No account, no problem. Send us an email from the address you used to sign up (or from an event you
            hosted / participated in) to{" "}
            <a href="mailto:privacy@galeriecop.com" className="text-coral-400 hover:text-coral-500">privacy@galeriecop.com</a>.
            We&apos;ll verify ownership and process your request within <strong>30 days</strong> (usually within 5
            business days).
          </p>
          <p className="mt-3">
            <a
              href="mailto:privacy@galeriecop.com?subject=Data%20deletion%20request"
              data-testid="deletion-email-cta"
              className="inline-flex items-center gap-2 border border-navy-700 text-navy-700 hover:bg-navy-700 hover:text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors"
            >
              <Mail className="w-4 h-4" /> Email a deletion request
            </a>
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">3. Guest photo take-downs</h2>
          <p>
            If you appear in a photo on someone else&apos;s gallery and want it removed, the fastest path is to email
            the event host directly — their contact is usually visible on the event page. If you can&apos;t find them,
            email us at <a href="mailto:privacy@galeriecop.com" className="text-coral-400 hover:text-coral-500">privacy@galeriecop.com</a>{" "}
            with the event gallery link and we&apos;ll forward the request.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">4. What we keep, and for how long</h2>
          <p>Even after your account is deleted, we retain a minimal set of records where the law requires:</p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm border border-cream-200 rounded-xl overflow-hidden">
              <thead className="bg-cream-100 text-left">
                <tr>
                  <th className="p-3 font-semibold">Data</th>
                  <th className="p-3 font-semibold">Retention</th>
                  <th className="p-3 font-semibold">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream-200">
                <tr>
                  <td className="p-3">Account, events, uploads, RSVPs, tokens</td>
                  <td className="p-3">Deleted immediately on request</td>
                  <td className="p-3">Your data — your call</td>
                </tr>
                <tr>
                  <td className="p-3">Backups containing the above</td>
                  <td className="p-3">Purged within 30 days</td>
                  <td className="p-3">Disaster-recovery rotation</td>
                </tr>
                <tr>
                  <td className="p-3">Payment records (transaction ids, amounts)</td>
                  <td className="p-3">6 years from transaction date</td>
                  <td className="p-3">Statutory tax &amp; accounting obligations</td>
                </tr>
                <tr>
                  <td className="p-3">Erasure audit log (hashed identifiers only, no PII)</td>
                  <td className="p-3">6 years</td>
                  <td className="p-3">Regulator audit trail</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">5. Automatic deletion on event expiry</h2>
          <p>
            Uploaded photos and videos are kept while the event tier is active. After a tier expires and is not
            renewed, media is auto-deleted <strong>90 days</strong> after expiry. Expiry emails go out at 14, 7 and
            1 day(s) before deletion so hosts have plenty of notice to download or renew.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">6. Download your data first (optional)</h2>
          <p>
            Before deleting, we recommend using <strong>Download my data</strong> on the dashboard to grab a JSON
            export of your account, events, uploads metadata, payments and RSVPs. Media itself can be exported
            per-event via the &quot;Download all (ZIP)&quot; button on each event&apos;s dashboard.
          </p>
          <p className="mt-3">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 bg-navy-700 hover:bg-navy-800 text-white font-semibold px-5 py-2.5 rounded-full text-sm"
            >
              <Download className="w-4 h-4" /> Download my data
            </Link>
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">7. Contact</h2>
          <p>
            Data-related questions or complaints? Email{" "}
            <a href="mailto:privacy@galeriecop.com" className="text-coral-400 hover:text-coral-500">privacy@galeriecop.com</a>.
            You may also lodge a complaint with the UK Information Commissioner&apos;s Office at ico.org.uk.
          </p>
        </section>
      </div>
    </div>
  </section>
);

export default DataDeletionPolicyPage;
